import { useMemo } from 'react';

/**
 * Hook per calcolare la differenza in giorni tra la data odierna e una data di creazione
 * @param creationDate - Data di creazione nel formato ISO string
 * @returns Oggetto con il risultato del calcolo e informazioni di debug
 */
export const useDaysDifference = (creationDate: string | null | undefined) => {
  const result = useMemo(() => {
    if (!creationDate) {
      return {
        daysDifference: 0,
        isValid: false,
        error: 'No creation date provided'
      };
    }

    try {
      // Parsing della data di creazione
      const creationDateObj = new Date(creationDate);
      
      // Verifica che la data sia valida
      if (isNaN(creationDateObj.getTime())) {
        return {
          daysDifference: 0,
          isValid: false,
          error: 'Invalid date format'
        };
      }
      
      // Data odierna
      const today = new Date();
      
      // Reset delle ore per confrontare solo le date
      // Usiamo UTC per evitare problemi di fuso orario
      const creationDateOnly = new Date(Date.UTC(
        creationDateObj.getUTCFullYear(), 
        creationDateObj.getUTCMonth(), 
        creationDateObj.getUTCDate()
      ));
      const todayOnly = new Date(Date.UTC(
        today.getUTCFullYear(), 
        today.getUTCMonth(), 
        today.getUTCDate()
      ));
      
      // Calcolo della differenza in millisecondi
      const differenceInMs = todayOnly.getTime() - creationDateOnly.getTime();
      
      // Conversione in giorni
      const differenceInDays = Math.floor(differenceInMs / (1000 * 60 * 60 * 24));
      
      // Aggiunta di 1 al risultato
      const finalResult = differenceInDays + 1;
      
      // Log di debug completo con date prima e dopo conversione
      console.log('[DEBUG] useDaysDifference Hook - CALCOLO COMPLETO:', {
        // Input originale
        input: {
          creationDate: creationDate,
          creationDateType: typeof creationDate,
          creationDateLength: creationDate?.length
        },
        
        // Parsing e validazione
        parsing: {
          creationDateObj: creationDateObj.toISOString(),
          creationDateObjLocal: creationDateObj.toString(),
          creationDateObjTime: creationDateObj.getTime(),
          isValidDate: !isNaN(creationDateObj.getTime())
        },
        
        // Date odierne
        today: {
          todayFull: today.toISOString(),
          todayLocal: today.toString(),
          todayTime: today.getTime()
        },
        
        // Date normalizzate (solo data, senza ora)
        normalized: {
          creationDateOnly: creationDateOnly.toISOString(),
          creationDateOnlyLocal: creationDateOnly.toString(),
          creationDateOnlyTime: creationDateOnly.getTime(),
          todayOnly: todayOnly.toISOString(),
          todayOnlyLocal: todayOnly.toString(),
          todayOnlyTime: todayOnly.getTime()
        },
        
        // Calcoli
        calculations: {
          differenceInMs: differenceInMs,
          differenceInDays: differenceInDays,
          finalResult: finalResult,
          formula: `${differenceInMs}ms / (1000 * 60 * 60 * 24) = ${differenceInDays} giorni + 1 = ${finalResult}`
        },
        
        // Verifiche
        verification: {
          isPositiveDifference: differenceInMs > 0,
          isReasonableDays: differenceInDays >= 0 && differenceInDays <= 36500, // Max 100 anni
          isReasonableResult: finalResult >= 1 && finalResult <= 36501
        }
      });
      
      return {
        daysDifference: finalResult,
        isValid: true,
        error: null,
        debug: {
          creationDate,
          creationDateObj: creationDateObj.toISOString(),
          creationDateOnly: creationDateOnly.toISOString(),
          today: today.toISOString(),
          todayOnly: todayOnly.toISOString(),
          differenceInMs,
          differenceInDays,
          finalResult
        }
      };
    } catch (error) {
      console.error('[ERROR] useDaysDifference Hook Error:', error);
      return {
        daysDifference: 0,
        isValid: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        debug: null
      };
    }
  }, [creationDate]);

  return result;
};

/**
 * Hook semplificato che restituisce solo il numero di giorni
 * @param creationDate - Data di creazione nel formato ISO string
 * @returns Numero di giorni di differenza + 1
 */
export const useSimpleDaysDifference = (creationDate: string | null | undefined): number => {
  const { daysDifference } = useDaysDifference(creationDate);
  return daysDifference;
}; 